import React, { useState, useEffect, useCallback } from 'react';
import { getOrgContext } from '../utils/orgContext';

function OrgSettings() {
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({ name: '', email: '', phone: '', billing_email: '' });

  const orgCtx = getOrgContext();
  const token = localStorage.getItem('token');

  const fetchOrg = useCallback(async () => {
    if (!orgCtx?.organizationId) {
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/organizations/${orgCtx.organizationId}`, {
        headers: { Authorization: `Bearer ${token}`, 'X-Organization-Id': String(orgCtx.organizationId) }
      });
      if (!res.ok) throw new Error('Failed to load organization');
      const data = await res.json();
      setOrg(data.organization);
      setForm({
        name: data.organization.name || '',
        email: data.organization.email || '',
        phone: data.organization.phone || '',
        billing_email: data.organization.billing_email || ''
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [orgCtx?.organizationId, token]);

  useEffect(() => { fetchOrg(); }, [fetchOrg]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/organizations/${orgCtx.organizationId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Organization-Id': String(orgCtx.organizationId)
        },
        body: JSON.stringify(form)
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to update');
      }
      setSuccess('Organization updated.');
      fetchOrg();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <main className="App-main"><p>Loading...</p></main>;
  if (!orgCtx?.organizationId) {
    return (
      <main className="App-main">
        <div className="report-card" style={{ maxWidth: 600, margin: '2rem auto', padding: '2rem' }}>
          <h2>Organization Settings</h2>
          <p className="status-text">You are not part of an organization. Register as a Property Manager to create one.</p>
        </div>
      </main>
    );
  }

  return (
    <main className="App-main">
      <div style={{ maxWidth: 640, margin: '2rem auto', padding: '0 1rem' }}>
        <h2 style={{ marginBottom: '0.25rem' }}>Organization Settings</h2>
        <p className="status-text" style={{ marginBottom: '1.5rem' }}>{org?.slug || 'Manage your organization details'}</p>

        {error && <div className="form-error" style={{ marginBottom: '1rem' }}>{error}</div>}
        {success && <div className="form-success" style={{ marginBottom: '1rem' }}>{success}</div>}

        <div className="report-card" style={{ padding: '1.5rem' }}>
          <form onSubmit={handleSave}>
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label>Organization name</label>
              <input
                type="text"
                className="form-control"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label>Contact email</label>
              <input
                type="email"
                className="form-control"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label>Phone</label>
              <input
                type="tel"
                className="form-control"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label>Billing email</label>
              <input
                type="email"
                className="form-control"
                value={form.billing_email}
                onChange={(e) => setForm({ ...form, billing_email: e.target.value })}
              />
              <small className="status-text">Invoices and payment receipts will be sent here.</small>
            </div>
            <button type="submit" className="primary-button" disabled={saving} style={{ marginTop: '0.5rem' }}>
              {saving ? 'Saving...' : 'Save changes'}
            </button>
          </form>
        </div>

        <div className="report-card" style={{ padding: '1.5rem', marginTop: '1.5rem' }}>
          <h3 style={{ marginBottom: '0.5rem' }}>Plan</h3>
          <p style={{ margin: 0 }}>
            <span className="report-chip">{org?.plan?.toUpperCase() || 'STARTER'}</span>
            {' '}
            <span className="status-text">Up to {org?.max_properties || 10} properties, {org?.max_members || 2} team members</span>
          </p>
        </div>
      </div>
    </main>
  );
}

export default OrgSettings;
